import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import SearchInput from "../../../../../../components/base/input/search_input";
import ProductRelationCard from "../../../../../../components/app-specific/product-cards/product-relation-card";
import {Col} from "reactstrap";
import {MarkProductForCacheSectionNames} from "../../../../../../../core/constants/enums";
import {useParams} from "react-router-dom";

const ProductInformationRelationsView = ({loading: loadingProps, setIsCacheNeeded}) => {
    const params = useParams()
    const [loadingRelations, setLoadingRelations] = useState(true);
    const [data, setData] = useState([]);
    const [keywords, setKeywords] = useState('');
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingRelations, [loadingProps, loadingRelations])

    /**
     * Fetches the related parts of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductRelations = useCallback(async () => {
        setLoadingRelations(true);
        const typesResponse = await Api.getAllOperatorPartRelationTypes()
        const response = await Api.getOperatorPartRelations(params.id)
        if (!isMounted()) return;
        if (response?.resultFlag && typesResponse?.resultFlag) {
            const types = typesResponse.data ?? [];
            const aggregated = Object.fromEntries(types.map(e => [e.id, []]));
            response?.data?.forEach(e => {
                if (!types.find(t => t.id === e.type.id)) {
                    types.push(e.type);
                }
                aggregated[e.type.id] = [...(aggregated[e.type.id] ?? []), e.relatedPart]
            })
            setData(Object.entries(aggregated).map(([typeId, relatedParts]) => ({
                type: types.find(e => e.id === parseInt(typeId)),
                relatedParts,
            })));
        }
        setLoadingRelations(false);
    }, [isMounted, params.id])

    /**
     * With each change in id of the url params:
     * - fetches the product technical files from the server.
     */
    useEffect(() => {
        void getProductRelations();
    }, [getProductRelations])

    /**
     * Adds the given product to the given relation type of the current product.
     * @param product
     * @param relationType
     * @return {Promise<void>}
     */
    const addProductRelation = useCallback(async (product, relationType) => {
        const forApi = {
            currentPartId: parseInt(params.id),
            relatedPartId: product.id,
            relationTypeId: relationType.id,
        }
        const response = await Api.createOperatorPartRelation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            Api.markPartsForCache({partIds: [Number(params?.id)], sectionNames: [MarkProductForCacheSectionNames.relations]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setData(prevState => prevState.map(e => e.type.id === relationType.id
                ? {...e, relatedParts: [...(e.relatedParts ?? []), product]}
                : e
            ))
        }
    }, [isMounted, params.id, setIsCacheNeeded]);

    /**
     * Removes the given product from the given relation type of the current product.
     * @param product
     * @param relationType
     * @return {Promise<void>}
     */
    const removeProductRelation = useCallback(async (product, relationType) => {
        const forApi = {
            currentPartId: parseInt(params.id),
            relatedPartId: product.id,
            relationTypeId: relationType.id,
        }
        const response = await Api.removesOperatorPartRelation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {

            Api.markPartsForCache({partIds: [Number(params?.id)], sectionNames: [MarkProductForCacheSectionNames.relations]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setData(prevState => prevState.map(e => e.type.id === relationType.id
                ? {...e, relatedParts: e.relatedParts?.filter(e => e.id !== forApi.relatedPartId)}
                : e
            ))
        }
    }, [isMounted, params.id, setIsCacheNeeded])

    return (
        <>
            <div className={'part-info-paper w-100 mb-3'}>
                <Col xs={12}>
                    <SearchInput
                        autoSearch
                        loading={loading}
                        disabled={loading}
                        search={setKeywords}
                        label={'Filter by Part Number'}
                    />
                </Col>

            </div>
            <div className={'d-flex flex-column w-100'}>
                {
                    loading
                        ? Array(4).fill(null).map((_, e) => (
                            <div key={e} className={'loading extraLarge mb-3'}>
                                <div/>
                            </div>
                        ))
                        : data.map(relation => (
                            <ProductRelationCard
                                key={relation.type.id}
                                owningProductId={Number(params?.id)}
                                filters={keywords}
                                relation={relation}
                                add={addProductRelation}
                                remove={removeProductRelation}
                            />
                        ))
                }
            </div>

        </>
    )
}


export default ProductInformationRelationsView;
